/**
 * Assembles everything the Create Invoice form pre-fills itself with
 * (OR-1449).
 *
 * The point of the feature is that the user re-types nothing Orion already
 * knows, so this pulls from four places at once:
 *
 *   - the submission detail  → FNI, dates, submission type, account manager
 *   - the broker record      → the brokerage, its id, phone and split address
 *   - the quote              → premium, fees and commissions
 *   - the policy             → policy number
 *
 * The brokerage's **id** is the reason the broker is fetched at all: the
 * submission detail carries only the brokerage's name and a flattened
 * address string, and the Ascend Account routes are keyed by brokerage id.
 *
 * Each source fails independently. A missing quote or policy leaves those
 * fields blank rather than blocking the form, because the backend is the
 * one that decides whether a submission can be invoiced and it says so
 * with a 422 the caller renders.
 */
import { useCallback, useEffect, useState } from "react";
import {
    getAgent,
    getPolicyForSubmission,
    getPremiumSelection,
    getSubmission,
    listAgents,
} from "../../../api";
import type { AgentRow } from "../../../types/submission";
import type { SubmissionForm } from "../../../types/submission";

export interface InvoicePrefill {
    /** Null when the submission's broker has no brokerage on file — the
     *  Ascend Account routes can't be called without it. */
    brokerageId: string | null;
    brokerageName: string;
    brokeragePhone: string;
    brokerageStreet: string;
    brokerageCity: string;
    brokerageState: string;
    brokerageZip: string;

    /** Billing-user options: every broker at this brokerage. */
    brokers: AgentRow[];
    /** The submission's own broker, pre-selected as the billing user. */
    defaultBrokerId: string | null;

    submissionType: string;
    fni: string;
    fniStreet: string;
    fniCity: string;
    fniState: string;
    fniZip: string;

    accountManagerName: string;
    accountManagerEmail: string;
    policyNumber: string;
    effectiveDate: string;
    expirationDate: string;

    /** Quote money, in dollars. Null when there is no quote yet. */
    selectedPremium: number | null;
    policyFee: number | null;
    taxesPct: number | null;
    taxesAmount: number | null;
    surchargesAmount: number | null;
    /** Rates only — the dollar amounts are backend derivations it does
     *  not expose. On the ask list. */
    tricuraCommissionPct: number | null;
    agentCommissionPct: number | null;
}

export type PrefillState =
    | { status: "loading" }
    | { status: "ready"; prefill: InvoicePrefill }
    | { status: "error"; message: string };

/** The submission's mailing address wins; otherwise the first location. */
function resolveInsuredAddress(detail: SubmissionForm) {
    const primary = detail.locations?.[0];
    return {
        street: detail.accountBasics?.mailingAddress || primary?.address || "",
        city: primary?.city ?? "",
        state: primary?.state ?? "",
        zip: primary?.zip ?? "",
    };
}

/**
 * The quote form keeps its money and percent inputs as raw strings (they
 * are masked text fields), so anything read off `inputs` has to come
 * through here before it can be formatted or multiplied.
 */
function toNumber(raw: string | null | undefined): number | null {
    if (raw == null || raw.trim() === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

/** `YYYY-MM-DD` → `MM/DD/YYYY`, tolerant of null/undefined. */
function isoToUsDate(iso: string | null | undefined): string {
    if (!iso) return "";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return y && m && d ? `${m}/${d}/${y}` : "";
}

export function useInvoicePrefill(detail: SubmissionForm | null): PrefillState {
    const [state, setState] = useState<PrefillState>({ status: "loading" });
    const submissionId = detail?.id ?? "";
    const brokerId = detail?.brokerage?.brokerId ?? "";

    const load = useCallback(async () => {
        if (!detail || !submissionId) return;
        setState({ status: "loading" });

        // Independent sources: one missing piece must not blank the rest.
        // The raw submission is re-fetched for `effective_date`: it is a
        // DIFFERENT column from renewal_date (the invoice bills on the
        // former) and the shared detail form never mapped it.
        const [agent, quote, policy, rawSubmission] = await Promise.all([
            brokerId ? getAgent(brokerId).catch(() => null) : Promise.resolve(null),
            getPremiumSelection(submissionId).catch(() => null),
            getPolicyForSubmission(submissionId).catch(() => null),
            getSubmission(submissionId).catch(() => null),
        ]);

        const brokerage = agent?.brokerage ?? null;
        const brokerageId = brokerage?.id ?? null;
        const brokers = brokerageId
            ? await listAgents({ brokerage_id: brokerageId, limit: 100 }).catch(() => [])
            : [];

        const inputs = quote?.form.inputs ?? null;
        const derived = quote?.derived ?? null;
        const selectedPremium = toNumber(inputs?.selectedPremium);

        // NO derivation: "Surcharges" is a backend bundle
        // (`other_fees_cents`) whose composition the backend owns — PR #367
        // is changing it right now, which is exactly why the FE must not
        // reproduce the sum. Dash until the backend exposes the figure.
        // On the ask list for Sammy.
        const surcharges: number | null = null;

        const address = resolveInsuredAddress(detail);

        setState({
            status: "ready",
            prefill: {
                brokerageId,
                brokerageName: brokerage?.name ?? detail.brokerage?.brokerageName ?? "",
                brokeragePhone: brokerage?.phone ?? "",
                brokerageStreet: brokerage?.address ?? "",
                brokerageCity: brokerage?.city ?? "",
                brokerageState: brokerage?.state ?? "",
                brokerageZip: brokerage?.zip_code ?? "",

                brokers,
                defaultBrokerId: brokerId || null,

                submissionType: detail.accountBasics?.type ?? "",
                fni: detail.accountBasics?.fni ?? "",
                fniStreet: address.street,
                fniCity: address.city,
                fniState: address.state,
                fniZip: address.zip,

                accountManagerName: detail.accountBasics?.accountManagerName ?? "",
                accountManagerEmail: detail.accountBasics?.accountManagerEmail ?? "",
                policyNumber: policy?.policy_number ?? "",
                // The invoice bills on effective/expiration — effective is a
                // DIFFERENT column from the renewal date the form shows.
                effectiveDate: isoToUsDate(rawSubmission?.effective_date) || detail.accountBasics?.renewalDate || "",
                expirationDate: isoToUsDate(rawSubmission?.expiration_date) || detail.accountBasics?.expirationDate || "",

                selectedPremium,
                policyFee: toNumber(inputs?.techRmFee),
                taxesPct: toNumber(inputs?.surplusLinesTaxPct),
                taxesAmount: derived?.surplusLinesTax ?? null,
                surchargesAmount: surcharges,
                tricuraCommissionPct: toNumber(inputs?.tricuraCommissionPct),
                agentCommissionPct: toNumber(inputs?.agentCommissionPct),
            },
        });
    }, [detail, submissionId, brokerId]);

    useEffect(() => {
        load().catch((e: unknown) =>
            setState({ status: "error", message: e instanceof Error ? e.message : "Unknown error" }),
        );
    }, [load]);

    return state;
}
